import { useState } from "react";
import { palette } from "./pageStyles";
import { getItems, newId, saveItem } from "../backtest/labStore";
import { runLabCode } from "../backtest/pyLab";
import { makeSampleCandles } from "../backtest/sampleCandles";

export const LAB_STARTERS = {
  strategy: String.raw`# A strategy for the backtest list. Set position: 1 = long, -1 = short, 0 = flat (one per candle).
# param(...) values become number boxes in the list, so you can tune them there without opening this code.
import pandas as pd

FAST = param("fast", 10, 2, 400)      # name, default, min, max
SLOW = param("slow", 40, 3, 800)

df = pd.DataFrame(candles)
fast = df["close"].ewm(span=FAST, adjust=False).mean()
slow = df["close"].ewm(span=SLOW, adjust=False).mean()

position = (fast > slow).astype(int) - (fast < slow).astype(int)   # long above, short below

# drawing is optional: it only shows here in the editor
plot("EMA fast", fast, color="#a6c8ff")
plot("EMA slow", slow, color="#a497e6")
`,
  indicator: String.raw`# An indicator for no-code rules. Call output(values) with one number per candle.
# In the backtest list: "+ Rule strategy" -> pick this indicator, e.g. "Z-score crosses below -2".
import pandas as pd

PERIOD = param("period", 20, 2, 500)

df = pd.DataFrame(candles)
mean = df["close"].rolling(PERIOD).mean()
std = df["close"].rolling(PERIOD).std()
zscore = (df["close"] - mean) / std

output(zscore, "Value")          # more lines: output(other_values, "Signal")
`,
};

const st = {
  bar: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "8px 12px", borderTop: `1px solid ${palette.border}`, background: "rgba(127,195,218,0.05)" },
  field: { padding: "6px 8px", borderRadius: 4, border: `1px solid ${palette.border}`, background: "#050608", color: palette.text, fontSize: "0.8rem", outline: "none" },
  btn: { padding: "6px 12px", borderRadius: 4, border: "none", background: palette.accent, color: palette.accentText, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" },
  ghost: { padding: "5px 10px", borderRadius: 4, border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.76rem", cursor: "pointer" },
  small: { fontSize: "0.74rem", color: palette.mutedText },
};

function parsePackages(text) {
  return String(text || "")
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 12);
}

/** Bar under the code editor: add the current code to the backtest list, or save changes to the item being edited.
 *  `link` = {id|null, kind, name} when the editor was opened from the list. */
export default function AddToBacktest({ code, packagesText, candles, link, setLink }) {
  const [kind, setKind] = useState(link?.kind || "strategy");
  const [name, setName] = useState("");
  const [status, setStatus] = useState({ busy: false, text: "", bad: false });
  const editing = link?.id ? getItems().find((it) => it.id === link.id) : null;
  const activeKind = editing ? editing.kind : link?.kind || kind;

  const submit = async (asNew) => {
    const finalName = (editing && !asNew ? editing.name : name || link?.name || "").trim();
    if (!finalName) {
      setStatus({ busy: false, text: "Give it a name first.", bad: true });
      return;
    }
    setStatus({ busy: true, text: "Checking the code with Python (the first time takes about 10-20 s)...", bad: false });
    const testCandles = candles && candles.length >= 60 ? candles : makeSampleCandles({ count: 500 });
    const packages = parsePackages(packagesText);
    try {
      const res = await runLabCode({ code, candles: testCandles, paramValues: {}, packages });
      if (activeKind === "strategy" && !res.position) {
        throw new Error('The code never sets position = ... (1 long, -1 short, 0 flat, one per candle). Add that line, or add it as an indicator instead.');
      }
      const outputNames = Object.keys(res.outputs);
      if (activeKind === "indicator" && !outputNames.length) {
        throw new Error('The code never calls output(values). Add e.g. output(my_line) so rules can use it.');
      }
      const saved = saveItem({
        id: editing && !asNew ? editing.id : newId("code"),
        kind: activeKind,
        name: finalName,
        code,
        params: res.params,
        outputs: outputNames,
        packages,
      });
      setLink({ id: saved.id, kind: saved.kind, name: saved.name });
      setName("");
      const where = saved.kind === "strategy" ? '"My strategies"' : '"My indicators" (use it in "+ Rule strategy")';
      const p = saved.params.length ? ` Settings in the list: ${saved.params.map((x) => x.label).join(", ")}.` : "";
      setStatus({ busy: false, text: `${editing && !asNew ? "Saved" : "Added"} "${saved.name}" to ${where}.${p}`, bad: false });
    } catch (err) {
      setStatus({ busy: false, text: err.message, bad: true });
    }
  };

  return (
    <div style={st.bar} role="group" aria-label="Add this code to the backtest">
      {editing ? (
        <>
          <span style={{ fontSize: "0.8rem" }}>
            Editing <strong>{editing.name}</strong> <span style={st.small}>({editing.kind === "strategy" ? "my strategy" : "my indicator"})</span>
          </span>
          <button type="button" style={st.btn} onClick={() => submit(false)} disabled={status.busy}>Save changes</button>
          <input style={{ ...st.field, width: 150 }} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="New name" aria-label="Name for a copy" />
          <button type="button" style={st.ghost} onClick={() => submit(true)} disabled={status.busy || !name.trim()}>Save as copy</button>
          <button type="button" style={st.ghost} onClick={() => setLink(null)} disabled={status.busy}>Stop editing</button>
        </>
      ) : (
        <>
          <span style={{ fontSize: "0.8rem" }}>Add to backtest as</span>
          <div role="radiogroup" aria-label="Add as" style={{ display: "flex", gap: 2, padding: 2, border: `1px solid ${palette.border}`, borderRadius: 4 }}>
            {[
              ["strategy", "Strategy"],
              ["indicator", "Indicator"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={activeKind === id}
                onClick={() => {
                  setKind(id);
                  if (link && !link.id) setLink({ ...link, kind: id });
                }}
                style={{ ...st.ghost, border: "none", background: activeKind === id ? palette.selected : "transparent" }}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            style={{ ...st.field, width: 170 }}
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit(true);
            }}
            placeholder={activeKind === "strategy" ? "Name, e.g. My EMA trend" : "Name, e.g. Z-score"}
            aria-label="Name in the backtest list"
          />
          <button type="button" style={st.btn} onClick={() => submit(true)} disabled={status.busy}>Add</button>
          <span style={st.small}>{activeKind === "strategy" ? "Needs position = ..." : "Needs output(values)"}</span>
        </>
      )}
      {status.text && (
        <span role="status" style={{ ...st.small, color: status.bad ? palette.danger : status.busy ? palette.mutedText : palette.up || palette.accent, flexBasis: "100%" }}>
          {status.text}
        </span>
      )}
    </div>
  );
}
